import { Sparkles, Zap, Crown, ArrowRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useCredits } from '@/hooks/useCredits';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

interface UpgradePromptProps {
  variant?: 'banner' | 'card' | 'inline' | 'modal';
  title?: string;
  description?: string;
  feature?: string;
  creditsNeeded?: number;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

export function UpgradePrompt({
  variant = 'inline',
  title,
  description,
  feature,
  creditsNeeded,
  dismissible = true,
  onDismiss,
  className = ''
}: UpgradePromptProps) {
  const { balance, plan, isCriticalBalance } = useCredits();
  const navigate = useNavigate();
  const [isDismissed, setIsDismissed] = useState(false);

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  if (isDismissed) return null;

  const insufficientCredits = creditsNeeded !== undefined && balance < creditsNeeded;
  const isFreePlan = plan?.is_free ?? true;

  const defaultTitle = insufficientCredits 
    ? "You don't have enough credits" 
    : isFreePlan 
      ? 'Unlock more with Pro' 
      : 'Need more credits?';

  const defaultDescription = insufficientCredits
    ? `This action requires ${creditsNeeded} credit${creditsNeeded > 1 ? 's' : ''}, but you only have ${balance}.`
    : isFreePlan
      ? 'Upgrade to get more credits and unlock premium features.'
      : 'Add more credits to continue using all features.';

  if (variant === 'banner') {
    return (
      <div className={`relative bg-gradient-to-r from-primary/10 via-primary/5 to-accent/10 border-y border-primary/20 py-3 px-4 ${className}`}>
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/20 rounded-full">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="font-medium text-sm">{title || defaultTitle}</p>
              <p className="text-xs text-muted-foreground">{description || defaultDescription}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              onClick={() => navigate('/pricing')}
              className="gap-1"
            >
              <Zap className="h-3 w-3" />
              {isFreePlan ? 'Upgrade' : 'Add Credits'}
            </Button>
            {dismissible && (
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={handleDismiss}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <Card className={`relative overflow-hidden border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5 ${className}`}>
        {dismissible && (
          <Button 
            variant="ghost" 
            size="icon" 
            className="absolute top-2 right-2 h-6 w-6"
            onClick={handleDismiss}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
        
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary/20 rounded-xl">
              <Crown className="h-6 w-6 text-primary" />
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold">{title || defaultTitle}</h3>
                {insufficientCredits && (
                  <Badge variant="destructive" className="text-xs">
                    Need {creditsNeeded - balance} more
                  </Badge>
                )}
              </div>
              
              <p className="text-sm text-muted-foreground mb-4">
                {description || defaultDescription}
              </p>

              {feature && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <span>Unlock: <strong className="text-foreground">{feature}</strong></span>
                </div>
              )}
              
              <Button 
                onClick={() => navigate('/pricing')}
                className="gap-2"
                variant={isCriticalBalance || insufficientCredits ? 'destructive' : 'default'}
              >
                {isFreePlan ? 'View Plans' : 'Get More Credits'}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  if (variant === 'modal') {
    return (
      <div className={`fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm ${className}`}>
        <Card className="relative max-w-md w-full mx-4 overflow-hidden border-primary/20">
          {dismissible && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="absolute top-3 right-3"
              onClick={handleDismiss}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
          
          <div className="p-8 text-center">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mb-4">
              <Crown className="h-8 w-8 text-primary-foreground" />
            </div>
            
            <h2 className="text-xl font-bold mb-2">{title || defaultTitle}</h2>
            <p className="text-muted-foreground mb-6">{description || defaultDescription}</p>

            {insufficientCredits && (
              <div className="flex items-center justify-center gap-4 mb-6 p-4 bg-muted rounded-lg">
                <div className="text-center">
                  <p className="text-2xl font-bold text-destructive">{balance}</p>
                  <p className="text-xs text-muted-foreground">Available</p>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <div className="text-center">
                  <p className="text-2xl font-bold">{creditsNeeded}</p>
                  <p className="text-xs text-muted-foreground">Required</p>
                </div>
              </div>
            )}
            
            <div className="flex flex-col gap-2">
              <Button 
                size="lg"
                onClick={() => navigate('/pricing')}
                className="gap-2"
              >
                <Sparkles className="h-4 w-4" />
                {isFreePlan ? 'View Upgrade Options' : 'Get More Credits'}
              </Button>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" size="sm" onClick={() => navigate('/')}>
                  Dashboard
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigate('/tailored-resume-library')}>
                  My Resumes
                </Button>
              </div>
              {dismissible && (
                <Button variant="ghost" onClick={handleDismiss}>
                  Maybe Later
                </Button>
              )}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Inline variant (default)
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20 ${className}`}>
      <Sparkles className="h-4 w-4 text-primary flex-shrink-0" />
      <p className="text-sm flex-1">
        {title || defaultTitle}
        {description && <span className="text-muted-foreground ml-1">{description}</span>}
      </p>
      <Button 
        size="sm" 
        variant="outline"
        onClick={() => navigate('/pricing')}
        className="flex-shrink-0 gap-1"
      >
        {isFreePlan ? 'Upgrade' : 'Add Credits'}
        <ArrowRight className="h-3 w-3" />
      </Button>
    </div>
  );
}